import { isObject } from './is';
import type { DeepMerge } from './types';

/**
 * Strict typed `Object.keys`
 *
 * @category Object
 */
export function objectKeys<T extends object>(obj: T) {
	return Object.keys(obj) as Array<`${keyof T & (string | number | boolean | null | undefined)}`>;
}

/**
 * Strict typed `Object.entries`
 *
 * @category Object
 */
export function objectEntries<T extends object>(obj: T) {
	return Object.entries(obj) as Array<[keyof T, T[keyof T]]>;
}

/**
 * Deep merge
 *
 * The first argument is the target object, the rest are the sources.
 * The target object will be mutated and returned.
 *
 * @category Object
 */
export function deepMerge<T extends object = object, S extends object = T>(target: T, ...sources: S[]): DeepMerge<T, S> {
	if (!sources.length) return target as DeepMerge<T, S>;

	const source = sources.shift();
	if (source === undefined) return target as DeepMerge<T, S>;

	if (isMergableObject(target) && isMergableObject(source)) {
		objectKeys(source).forEach((key) => {
			if (key === '__proto__' || key === 'constructor' || key === 'prototype') return;

			// @ts-expect-error
			if (isMergableObject(source[key])) {
				// @ts-expect-error
				if (!target[key])
					// @ts-expect-error
					target[key] = {};

				// @ts-expect-error
				deepMerge(target[key], source[key]);
			} else {
				// @ts-expect-error
				target[key] = source[key];
			}
		});
	}

	return deepMerge(target, ...sources);
}

/**
 * Deep merge
 *
 * Differs from `deepMerge` in that it merges arrays instead of overriding them.
 *
 * The first argument is the target object, the rest are the sources.
 * The target object will be mutated and returned.
 *
 * @category Object
 */
export function deepMergeWithArray<T extends object = object, S extends object = T>(target: T, ...sources: S[]): DeepMerge<T, S> {
	if (!sources.length) return target as DeepMerge<T, S>;

	const source = sources.shift();
	if (source === undefined) return target as DeepMerge<T, S>;

	if (Array.isArray(target) && Array.isArray(source)) target.push(...source);

	if (isMergableObject(target) && isMergableObject(source)) {
		objectKeys(source).forEach((key) => {
			if (key === '__proto__' || key === 'constructor' || key === 'prototype') return;

			// @ts-expect-error
			if (Array.isArray(source[key])) {
				// @ts-expect-error
				if (!target[key])
					// @ts-expect-error
					target[key] = [];

				// @ts-expect-error
				deepMergeWithArray(target[key], source[key]);
			}
			// @ts-expect-error
			else if (isMergableObject(source[key])) {
				// @ts-expect-error
				if (!target[key])
					// @ts-expect-error
					target[key] = {};

				// @ts-expect-error
				deepMergeWithArray(target[key], source[key]);
			} else {
				// @ts-expect-error
				target[key] = source[key];
			}
		});
	}

	return deepMergeWithArray(target, ...sources);
}

function isMergableObject(item: unknown): item is object {
	return isObject(item) && !Array.isArray(item);
}

/**
 * Create a new subset object by giving keys
 *
 * @category Object
 */
export function objectPick<O extends object, T extends keyof O>(obj: O, keys: T[], omitUndefined = false) {
	return keys.reduce(
		(n, k) => {
			if (k in obj) {
				if (!omitUndefined || obj[k] !== undefined) n[k] = obj[k];
			}
			return n;
		},
		{} as Pick<O, T>
	);
}

/**
 * Clear undefined fields from an object. It mutates the object
 *
 * @category Object
 */
export function clearUndefined<T extends object>(obj: T): T {
	Object.keys(obj).forEach((key: string) => ((obj as Record<string, unknown>)[key] === undefined ? delete (obj as Record<string, unknown>)[key] : {}));
	return obj;
}
